"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { setConsentLevel, type ConsentLevel } from "@/utils/cookie-actions";
import { getCookie, setCookie } from "@/utils/cookie-utils";
import { Cookie, X } from "lucide-react";

export function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consentCookie = getCookie("cookie-consent");
    setShow(!consentCookie);
  }, []);

  if (!show) return null;

  const handleConsent = async (level: ConsentLevel) => {
    setCookie("cookie-consent", level, {
      maxAge: 365 * 24 * 60 * 60, // 1 year
      path: "/",
      sameSite: "lax",
      secure: false,
    });

    setCookie("first-visit", "false", {
      maxAge: 365 * 24 * 60 * 60,
      path: "/",
      sameSite: "lax",
      secure: false,
    });

    try {
      await setConsentLevel(level);
    } catch (e) {
      console.warn("Failed to sync cookie consent to server session:", e);
    }

    setShow(false);
  };

  if (showDetails) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-semibold text-lg">Cookie Preferences</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetails(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4 text-sm text-muted-foreground">
              <p>Choose your cookie preferences:</p>

              <div className="space-y-3">
                <Button
                  onClick={() => handleConsent("essential")}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <div className="text-left">
                    <div className="font-medium text-foreground">
                      Essential Only
                    </div>
                    <div className="text-xs">
                      Required for basic functionality
                    </div>
                  </div>
                </Button>

                <Button
                  onClick={() => handleConsent("analytics")}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <div className="text-left">
                    <div className="font-medium text-foreground">Analytics</div>
                    <div className="text-xs">
                      Help us improve the experience
                    </div>
                  </div>
                </Button>

                <Button
                  onClick={() => handleConsent("all")}
                  className="w-full justify-start"
                >
                  <div className="text-left">
                    <div className="font-medium">Accept All</div>
                    <div className="text-xs">
                      Best experience with all features
                    </div>
                  </div>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50">
      <Card className="shadow-lg border">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Cookie className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-3">
              <div>
                <h3 className="font-medium">We use cookies</h3>
                <p className="text-sm text-muted-foreground">
                  We use cookies to enhance your experience and analyze usage.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => handleConsent("all")} size="sm">
                  Accept All
                </Button>
                <Button
                  onClick={() => handleConsent("essential")}
                  variant="outline"
                  size="sm"
                >
                  Essential Only
                </Button>
                <Button
                  onClick={() => setShowDetails(true)}
                  variant="ghost"
                  size="sm"
                >
                  Customize
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
